import React, { useEffect, useState } from "react";
import t, { changeLanguage } from "../../../components/I18N/TranslateHelpers";
import { GetCurrentLocale } from "../../../components/Utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/RootReducer";
import { LocalParams } from "../../../components/LocalParams";
import { getCustomerOrders } from "../../Order/OrderSlice";
import { SpinnerOnTop } from "../../../components/SpinnerOnTop";
import { buttonHtmlAddItemToCart, variantExistsInCart } from "../../../components/UtilsForCart";
import Toaster from "../../../components/Toaster";
import { canUserSeeProduct } from "../../../components/Catalogue/userCatalogUtils";
import DataTable from 'react-data-table-component';


interface OrderItemRow {
  product: string;
  reference: string;
  price: string;
  action: JSX.Element;
}

const OrdersPage = () => {
  const locale = GetCurrentLocale();
  changeLanguage(locale);
  const isAuthenticated = useSelector((state: RootState) => state.userRdc.isAuthenticated);
  const dispatch = useDispatch();
  const token = LocalParams.getItem('token') as string;
  const isRequesting = useSelector((state: RootState) => state.orderRdc.isRequesting);
  const isAddToCartSuccess = useSelector((state:RootState) => state.cartRdc.isRequestSuccess);
  const cardAddSuccess = useSelector((state:RootState) => state.cartRdc.isAddSuccess);
  const userAccount = useSelector((state:RootState) => state.userRdc.userDetail?.customer)
  const [orderView, setViewOrder] = useState<string | null>(null);
  const itemsInCart = useSelector((state: RootState) => state.cartRdc.cart);
  const [showToaster, setShowToaster] = useState(false);
  const [isToasterForCart, setIsToasterForCart] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      dispatch(getCustomerOrders(token));
    }
  }, [isAuthenticated, token]);

  const orders: any[] = useSelector((state: RootState) => state.orderRdc.orders) || [];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  const sortedOrders = orders.slice().sort((a, b) => a.number.localeCompare(b.number));

  const summaryData = sortedOrders.map((order) => ({
    orderNumber: order.number,
    date: formatDate(new Date(order.checkoutCompletedAt)),
    productCount: order.items.length,
    viewButton: (
      <button onClick={() => setViewOrder(order.number)} className="btn btn-primary">
        {t("view_order")}
      </button>
    ),
  }));

  const orderDetails = sortedOrders.find(order => order.number === orderView);
  const userCatalogs = useSelector((state: RootState) => state.userRdc.userCatalog);

  const customStyles = {
    headCells: {
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    cells: {
      style: {
        fontSize: '14px'
      }
    }
  };

  return isRequesting ? (
    <SpinnerOnTop />
  ) : (
    <>
      {/* 1. Liste des commandes si aucune commande n’est sélectionnée */}
      {orderView === null ? (
        <>
          <DataTable
              columns={[
                { name: t('order_number'), selector: row => row.orderNumber, sortable: true },
                { name: t('date'), selector: row => row.date },
                { name: t('number_of_product'), selector: row => row.productCount },
                { name: t('action'), cell: row => row.viewButton }
              ]}
              data={summaryData}
              pagination={false}
              highlightOnHover
              striped
              customStyles={customStyles}
          />
        </>
      ) : (
        <>
          {/* 2. Détails d'une commande sélectionnée */}
          <button
            onClick={() => setViewOrder(null)}
            className="btn btn-secondary float-end btn-light btn-sm"
          >
            {t('back_to_order_list')}
          </button>

          {orderDetails && (
            <>
              <h4>
                {t("order_date", {
                  orderNumber: orderDetails.number,
                  date: formatDate(new Date(orderDetails.checkoutCompletedAt))
                })}
              </h4>

              <DataTable<OrderItemRow>
                  columns={[
                    {
                      name: t('article'),
                      selector: row => row.product,
                      sortable: true,
                    },
                    {
                      name: t('reference'),
                      selector: row => row.reference,
                      sortable: true,
                      width: '200px'
                    },
                    {
                      name: t('price'),
                      selector: row => row.price,
                      sortable: false,
                      width: '150px'
                    },
                    {
                      name: t('action'),
                      cell: row => row.action,
                      ignoreRowClick: true,
                      button: true,
                      width: '250px'
                    }
                  ]}
                  data={orderDetails.items.map((productOrdered: any) => {
                    const canSee = canUserSeeProduct(productOrdered.product.id, userCatalogs);
                    return {
                      product: productOrdered.product.name,
                      reference: productOrdered.variantCode,
                      price: canSee
                          ? (productOrdered.unitPrice / 100).toFixed(2) + ` € ${t('htva')}`
                          : '–',
                      action: buttonHtmlAddItemToCart(
                          !variantExistsInCart(productOrdered.variantCode, itemsInCart?.items ?? []),
                          productOrdered.variantCode,
                          1,
                          locale,
                          dispatch,
                          setShowToaster,
                          setIsToasterForCart,
                          cardAddSuccess,
                          userAccount?.email ?? null,
                          productOrdered.id
                      )
                    };
                  })}
                  pagination={false}
                  highlightOnHover
                  striped
                  responsive
                  customStyles={customStyles}
              />
            </>
          )}
        </>
      )}

      {isAddToCartSuccess && (
        <Toaster
          showToaster={showToaster}
          setShowToaster={setShowToaster}
          isToasterForCart={isToasterForCart}
          setIsToasterForCart={setIsToasterForCart}
        />
      )}
    </>
  );
}

export default OrdersPage;
